import { massToRadius, SNAKE } from '../constants';
import type { Camera } from '../engine/Camera';
import { sampleSpline } from '../utils/spline';
import { clamp, type Vec2 } from '../utils/math';
import {
  remoteSnakes,
  getRosterEntry,
  type RemoteSnake,
} from '../../net/liveSession';

/**
 * Phase-2-Lite renderer for remote players' snakes.
 *
 * Distinct from SnakeRenderer because remote snakes have a different
 * data model: no Snake instance, no pulse/chomp/tongue/orb state, no
 * AI, just a stream of position snapshots from the relay. The visual
 * style is INTENTIONALLY the same — a remote player is meant to look
 * indistinguishable from a local snake; the player simply sees more
 * activity in the arena. Phase 2 Full will unify these into a single
 * server-authoritative render path; for now we keep two passes side
 * by side.
 *
 * Interpolation strategy:
 *
 *   We render with a fixed 100 ms behind-the-wall-clock delay. That
 *   lets the renderer always pick an alpha ∈ [0, 1] between the prev
 *   and curr snapshots (both already received), instead of trying to
 *   extrapolate forward past the latest snapshot. Smooth motion at
 *   60 fps from 10 Hz updates, no over-prediction artifacts.
 *
 *   First-update case (prevReceivedAt === 0): no interpolation,
 *   render at the curr snapshot directly. This produces a tiny
 *   one-update "pop" when a new player joins, which we hide with a
 *   200 ms fade-in.
 *
 * Body reconstruction:
 *
 *   The relay sends every 3rd segment (sparse sample). Catmull-Rom
 *   spline through the received points reconstructs a smooth body —
 *   the same code path the local SnakeRenderer uses. Visually
 *   indistinguishable at any zoom level since the original segment
 *   chain is itself a discrete approximation of a smooth path.
 *
 * Skipped for Phase 2 Lite (live Phase 2 Full work):
 *
 *   - Eyes, tongue flicks, chomp animation — need state we don't relay
 *   - Boost ignition trail particles — need pooled particle access
 *   - Digesting orbs — visual flavor, not gameplay critical
 *   - Leader aura — local computation only sees local snakes
 *   - Name-tag dynamic shadow/glow flicker — minor polish
 *
 * Performance: O(N × M) where N = remote snake count, M = sparse
 * sample count (~10-30 per snake). At 15 players × 20 segments × 4
 * samples-per-segment = 1200 spline samples per frame. Negligible
 * next to the local-engine cost.
 */

/** Render delay in ms — how far behind realtime we draw remote
 *  snakes. Trades a tiny latency for smooth interpolation.
 *  100 ms matches the 10 Hz send interval: prev + curr always
 *  bracket renderTime once we've received at least two updates. */
const RENDER_DELAY_MS = 100;

/** Spline samples per segment, matches SnakeRenderer for visual
 *  consistency. Sparse remote segments × this multiplier produces a
 *  smooth body curve. */
const SAMPLES_PER_SEGMENT = 4;

/** Below this on-screen radius, drop to a plain stroked-line render
 *  (no gradient discs). Saves cycles for distant remote snakes. */
const LOD_RADIUS_PX = 6;

/** Fade-in duration on first appearance — masks the one-update
 *  "pop" when a player joins or comes back from a TTL retirement. */
const FADE_IN_MS = 200;

export class RemoteSnakeRenderer {
  /** Scratch buffers reused across draw calls to keep allocations zero. */
  private readonly pts: Vec2[] = [];
  private readonly samples: Vec2[] = [];
  private readonly xs: number[] = [];
  private readonly ys: number[] = [];

  /**
   * Draw every remote snake currently in the registry.
   *
   * @param localSessionId  the local player's sessionId; if a remote
   *   entry somehow has the same id (shouldn't happen with the
   *   server's `{except: sender}` rule, but defensive), skip it.
   */
  drawAll(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    time: number,
    localSessionId: string | null,
  ): void {
    if (remoteSnakes.size === 0) return;
    const renderTime = Date.now() - RENDER_DELAY_MS;
    for (const remote of remoteSnakes.values()) {
      if (remote.id === localSessionId) continue; // defensive
      this.drawOne(ctx, camera, remote, renderTime, time);
    }
  }

  /** Pull one snake out of the registry and render at the
   *  interpolated state for the current renderTime. */
  private drawOne(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    r: RemoteSnake,
    renderTime: number,
    time: number,
  ): void {
    const roster = getRosterEntry(r.id);
    // Default to a neutral cyan if the roster snapshot hasn't arrived
    // yet (race: snake_state can land before the corresponding
    // snapshot patch). We still draw — the next snapshot will fix the
    // color within ~50 ms.
    const hue = roster?.hue ?? 200;
    const name = roster?.name ?? '';

    // ── Interpolate ──────────────────────────────────────────────
    // alpha ∈ [0, 1] between prev (α=0) and curr (α=1). When we don't
    // yet have a `prev` snapshot (first update from this sender),
    // render directly at curr.
    let alpha: number;
    let hx: number, hy: number, heading: number, mass: number;
    let useCurrSegmentsOnly: boolean;

    if (r.prevReceivedAt === 0) {
      alpha = 1;
      hx = r.hx; hy = r.hy; heading = r.heading; mass = r.mass;
      useCurrSegmentsOnly = true;
    } else {
      const span = r.receivedAt - r.prevReceivedAt;
      alpha = span > 0
        ? clamp((renderTime - r.prevReceivedAt) / span, 0, 1)
        : 1;
      hx = lerp(r.prevHx, r.hx, alpha);
      hy = lerp(r.prevHy, r.hy, alpha);
      heading = angularLerp(r.prevHeading, r.heading, alpha);
      mass = lerp(r.prevMass ?? r.mass, r.mass, alpha);
      useCurrSegmentsOnly = false;
    }

    // ── Build the spline control-point list ──────────────────────
    // Use interpolated segments when both prev and curr are available
    // AND have the same length (segments grow with mass; mismatched
    // lengths between updates are rare but possible). Fall back to
    // curr-only otherwise.
    const pts = this.pts;
    pts.length = 0;
    const curr = r.segments;
    const prev = r.prevSegments;
    if (useCurrSegmentsOnly || curr.length !== prev.length) {
      for (let i = 0; i < curr.length; i += 2) {
        pts.push({ x: curr[i]!, y: curr[i + 1]! });
      }
    } else {
      for (let i = 0; i < curr.length; i += 2) {
        pts.push({
          x: lerp(prev[i]!, curr[i]!, alpha),
          y: lerp(prev[i + 1]!, curr[i + 1]!, alpha),
        });
      }
    }
    if (pts.length < 2) return;

    // Prepend the head so the body actually starts where the head is
    // (the sparse sample's first point is typically near-head but may
    // not be exact after interpolation).
    pts.unshift({ x: hx, y: hy });

    const radius = massToRadius(mass);

    // ── Frustum cull ─────────────────────────────────────────────
    const bounds = camera.getVisibleBounds();
    const pad = radius * 1.8;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    if (maxX + pad < bounds.minX || minX - pad > bounds.maxX) return;
    if (maxY + pad < bounds.minY || minY - pad > bounds.maxY) return;

    // ── Sample the spline ────────────────────────────────────────
    const zoom = camera.zoom;
    const headRadPx = radius * zoom;
    const lod = headRadPx < LOD_RADIUS_PX;
    const density = lod ? 1 : SAMPLES_PER_SEGMENT;
    const samples = sampleSpline(pts, density, this.samples);

    // ── Project to screen ────────────────────────────────────────
    const cx = camera.viewportW * 0.5 + camera.shakeX;
    const cy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;
    const n = samples.length;
    const xs = this.xs;
    const ys = this.ys;
    while (xs.length < n) { xs.push(0); ys.push(0); }
    for (let i = 0; i < n; i++) {
      const s = samples[i]!;
      xs[i] = (s.x - cax) * zoom + cx;
      ys[i] = (s.y - cay) * zoom + cy;
    }

    // ── Fade-in alpha — masks the first-appearance pop ──────────
    const firstSeen = r.prevReceivedAt === 0 ? r.receivedAt : r.prevReceivedAt;
    const ageMs = Date.now() - firstSeen;
    const fadeAlpha = clamp(ageMs / FADE_IN_MS, 0, 1);
    const needsAlpha = fadeAlpha < 0.999;
    if (needsAlpha) {
      ctx.save();
      ctx.globalAlpha = fadeAlpha;
    }

    if (lod) {
      this.drawLOD(ctx, hue, xs, ys, n, radius);
    } else {
      this.drawBody(ctx, hue, xs, ys, n, radius, r.boosting, time);
      this.drawHead(ctx, hue, xs[0]!, ys[0]!, headRadPx, heading);
      if (name) this.drawNameLabel(ctx, name, hue, xs[0]!, ys[0]!, headRadPx);
    }

    if (needsAlpha) ctx.restore();

    // Mass on the remote object so next interpolation has a prev. We
    // store it AFTER reading so a partial update doesn't desync.
    r.prevMass = r.mass;
  }

  // ── Render passes ──────────────────────────────────────────────

  private drawLOD(
    ctx: CanvasRenderingContext2D,
    hue: number,
    xs: number[],
    ys: number[],
    n: number,
    radius: number,
  ): void {
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = `hsl(${hue} 100% 60%)`;
    ctx.lineWidth = Math.max(1.5, radius * 1.2);
    ctx.stroke();
  }

  private drawBody(
    ctx: CanvasRenderingContext2D,
    hue: number,
    xs: number[],
    ys: number[],
    n: number,
    radius: number,
    boosting: boolean,
    time: number,
  ): void {
    // Width tapers from head (~radius) to tail (~radius * TAIL_TAPER).
    const taper = SNAKE.TAIL_TAPER;

    // 1) Outer glow stroke — same neon-tube feel as local snakes.
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = `hsl(${hue} 100% 65%)`;
    ctx.shadowBlur = 20;
    ctx.strokeStyle = `hsla(${hue} 100% 65% / 0.42)`;
    ctx.lineWidth = radius * 2.4;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();
    ctx.restore();

    // 2) Body discs with the same brightness-wave look. Simpler than
    //    SnakeRenderer's full pass — no dark bands, no per-snake
    //    customization (we don't know the remote player's skin
    //    archetype, and copying that data over the wire would be
    //    wasteful for purely cosmetic detail).
    const phaseSpeed = 0.5 * time * Math.PI * 2;
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      const r = Math.max(1.2, radius * (1 - (1 - taper) * Math.pow(u, 1.2)));
      const phase = u * 5 * Math.PI * 2 - phaseSpeed;
      const tNorm = 0.5 + 0.5 * Math.sin(phase);
      const innerLight = clamp(54 + 28 * 0.9 + 28 * 0.5 * tNorm, 4, 96);
      const outerLight = clamp(54 - 28 * 0.6 - 16 - 6 * u, 2, 80);
      const sx = xs[i]!;
      const sy = ys[i]!;
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
      g.addColorStop(0, `hsl(${hue} 100% ${innerLight}%)`);
      g.addColorStop(0.55, `hsl(${hue} 100% ${Math.max(outerLight, innerLight * 0.55)}%)`);
      g.addColorStop(1, `hsl(${hue} 100% ${outerLight}%)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // 3) Boost rim — bright highlight when the remote player is
    //    boosting. Drives the "I can see them ignite" cue.
    if (boosting) {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = `hsla(${hue} 100% 78% / 0.85)`;
      ctx.shadowColor = `hsl(${hue} 100% 70%)`;
      ctx.shadowBlur = 12;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(xs[0]!, ys[0]!);
      for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
      ctx.stroke();
      ctx.restore();
    }
  }

  /** Minimal head — just a colored disc + a thin reptilian eye. No
   *  tongue, no chomp; those need state we don't have. */
  private drawHead(
    ctx: CanvasRenderingContext2D,
    hue: number,
    sx: number,
    sy: number,
    rPx: number,
    heading: number,
  ): void {
    // Head disc with a brighter inner gradient.
    const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, rPx);
    g.addColorStop(0, `hsl(${hue} 100% 78%)`);
    g.addColorStop(0.6, `hsl(${hue} 100% 58%)`);
    g.addColorStop(1, `hsl(${hue} 100% 32%)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(sx, sy, rPx, 0, Math.PI * 2);
    ctx.fill();

    // Single forward-looking eye marker — gives the head directional
    // affordance without faking full eye logic.
    const eyeR = Math.max(1.4, rPx * 0.22);
    const fx = Math.cos(heading);
    const fy = Math.sin(heading);
    const ex = sx + fx * rPx * 0.35;
    const ey = sy + fy * rPx * 0.35;
    ctx.fillStyle = `hsl(${hue} 60% 14%)`;
    ctx.beginPath();
    ctx.arc(ex, ey, eyeR, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawNameLabel(
    ctx: CanvasRenderingContext2D,
    name: string,
    hue: number,
    sx: number,
    sy: number,
    rPx: number,
  ): void {
    if (rPx < 8) return;
    const labelY = sy - rPx - 14;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 11px "Space Grotesk", system-ui, sans-serif';
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.lineWidth = 3;
    ctx.strokeText(name, sx, labelY);
    ctx.fillStyle = `hsl(${hue} 100% 78%)`;
    ctx.shadowColor = `hsla(${hue} 100% 60% / 0.6)`;
    ctx.shadowBlur = 6;
    ctx.fillText(name, sx, labelY);
    ctx.restore();
  }
}

// ─── Local helpers ─────────────────────────────────────────────────

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Interpolate between two angles via the shorter arc (handles wrap). */
function angularLerp(a: number, b: number, t: number): number {
  const TAU = Math.PI * 2;
  let d = ((b - a) % TAU + TAU) % TAU;
  if (d > Math.PI) d -= TAU;
  return a + d * t;
}
